import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useEffect, useState } from "react";
import { CapitalizationDocumentsService } from "../../../../shared/services/api/capitalization/CapitalizationDocumentsService";
import { CapitalizationDocumentType } from "../../../../shared/services/api/capitalization/types";
import { useToast } from "../../../../shared/hooks";
import { ToastContent } from "../../../../shared/components";
import { ModalPreviewDocument } from "../../../../shared/components/ModalPreviewDocument/ModalPreviewDocument";
import { LoadingComp } from "../../../../shared/components/loading/Loading";

interface ModalCapitalizationAttachmentsProps {
    open: boolean;
    onClose: () => void;
    capitalizationId: string;
}

export const ModalCapitalizationAttachments: React.FC<ModalCapitalizationAttachmentsProps> = ({
    open,
    onClose,
    capitalizationId,
}) => {
    const theme = useTheme();
    const toastHandler = useToast();
    const [documents, setDocuments] = useState<CapitalizationDocumentType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const fetchDocuments = async () => {
        if (!capitalizationId) return;
        setIsLoading(true);
        try {
            const result = await CapitalizationDocumentsService.getCapitalizationDocuments(capitalizationId);
            setDocuments(result);
        } catch (error) {
            console.error(error);
            toastHandler.present({
                type: "error",
                position: "top-right",
                messageOrContent: <ToastContent title="Erro" content="Erro ao buscar anexos" />,
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (open && capitalizationId) {
            fetchDocuments();
        }
    }, [open, capitalizationId]);

    const [selectedFiles, setSelectedFiles] = useState<{ file: File; description: string }[]>([]);

    const documentTypes = [
        "Proposta Assinada",
        "Boleto",
        "Comprovante de Pagamento",
        "Documentos Pessoais",
        "Outros"
    ];

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const newFiles = Array.from(files).map(file => ({
            file,
            description: "Outros" // Default description
        }));

        setSelectedFiles(prev => [...prev, ...newFiles]);

        // Clear input to allow selecting the same file again if needed
        event.target.value = "";
    };

    const handleDescriptionChange = (index: number, value: string) => {
        setSelectedFiles(prev => {
            const newFiles = [...prev];
            newFiles[index].description = value;
            return newFiles;
        });
    };

    const handleRemoveSelected = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleConfirmUpload = async () => {
        if (selectedFiles.length === 0 || !capitalizationId) return;

        setIsUploading(true);
        try {
            // Upload files sequentially
            for (const item of selectedFiles) {
                await CapitalizationDocumentsService.uploadCapitalizationDocument(
                    capitalizationId,
                    item.file,
                    item.description
                );
            }

            toastHandler.present({
                type: "success",
                position: "top-right",
                messageOrContent: <ToastContent title="Sucesso" content="Anexos enviados com sucesso" />,
            });

            setSelectedFiles([]); // Clear selection
            fetchDocuments(); // Refresh list
        } catch (error) {
            console.error(error);
            toastHandler.present({
                type: "error",
                position: "top-right",
                messageOrContent: <ToastContent title="Erro" content="Erro ao enviar alguns anexos" />,
            });
        } finally {
            setIsUploading(false);
        }
    };

    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

    const handleDelete = (documentId: string) => {
        setDocumentToDelete(documentId);
        setDeleteConfirmationOpen(true);
    };

    const confirmDelete = async () => {
        if (!documentToDelete) return;

        try {
            await CapitalizationDocumentsService.removeDocumentByCapitalization(documentToDelete);
            toastHandler.present({
                type: "success",
                position: "top-right",
                messageOrContent: <ToastContent title="Sucesso" content="Anexo removido com sucesso" />,
            });
            fetchDocuments();
        } catch (error) {
            console.error(error);
            toastHandler.present({
                type: "error",
                position: "top-right",
                messageOrContent: <ToastContent title="Erro" content="Erro ao remover anexo" />,
            });
        } finally {
            setDeleteConfirmationOpen(false);
            setDocumentToDelete(null);
        }
    };

    const [previewDoc, setPreviewDoc] = useState<CapitalizationDocumentType | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    const handleClosePreview = () => {
        setPreviewDoc(null);
        setPreviewUrl(null);
    };

    const handlePreviewFile = async (doc: CapitalizationDocumentType) => {
        setIsLoadingPreview(true);
        try {
            const blob = await CapitalizationDocumentsService.previewCapitalizationDocument(doc.id, doc.mimetype);
            if (blob instanceof Error) throw blob;

            const fileURL = URL.createObjectURL(blob);
            setPreviewDoc(doc);
            setPreviewUrl(fileURL);
        } catch {
            toastHandler.present({
                type: "error",
                position: "top-right",
                messageOrContent: <ToastContent title="Erro ao visualizar" content="Não foi possível abrir o documento." />,
            });
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleDownload = (doc: CapitalizationDocumentType) => {
        if (doc.url) {
            window.open(doc.url, "_blank");
        } else {
            toastHandler.present({
                type: "error",
                position: "top-right",
                messageOrContent: <ToastContent title="Erro" content="Caminho do arquivo não encontrado" />,
            });
        }
    };

    const handleDownloadAll = () => {
        if (documents.length === 0) {
            toastHandler.present({
                type: "warning",
                position: "top-right",
                messageOrContent: <ToastContent title="Atenção" content="Não há documentos para baixar" />,
            });
            return;
        }

        // Simulating download all by opening all files (temporary solution as no zip endpoint exists)
        // Ideally this should be a backend zip endpoint
        documents.forEach(doc => {
            if (doc.url) {
                const link = document.createElement('a');
                link.href = doc.url;
                link.download = doc.originalName || doc.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        });
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 1, overflow: "hidden" } }}
        >
            <LoadingComp isopen={isLoadingPreview} />
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: '400' }}>
                    Documentos da Capitalização
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {/* Info Header */}
                <Box sx={{
                    bgcolor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '70%' }}>
                        Para celeridade e regulação, favor anexar os documentos solicitados abaixo...
                        <br />
                        São aceitos documentos de até 50MB.
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<CloudUploadIcon sx={{ transform: 'rotate(180deg)' }} />}
                        onClick={handleDownloadAll}
                        sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
                    >
                        Baixar Anexos
                    </Button>
                </Box>

                {/* Section Header - Button Only */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
                    <Button
                        variant="outlined"
                        component="label"
                        color="primary"
                        startIcon={<CloudUploadIcon />}
                        disabled={isUploading}
                        sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
                    >
                        {isUploading ? "Enviando..." : "Selecionar Arquivos"}
                        <input type="file" hidden multiple onChange={handleFileSelect} />
                    </Button>
                </Box>

                {/* Selected Files List (Pending Upload) */}
                {selectedFiles.length > 0 && (
                    <Box sx={{ mb: 3, border: '1px dashed #ccc', borderRadius: 1, p: 2, bgcolor: '#fafafa' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Arquivos Selecionados ({selectedFiles.length})
                        </Typography>
                        <List dense>
                            {selectedFiles.map((item, index) => (
                                <ListItem key={index} divider>
                                    <Grid container alignItems="center" spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="body2" noWrap title={item.file.name}>
                                                {item.file.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {(item.file.size / 1024 / 1024).toFixed(2)} MB
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Descrição / Tipo</InputLabel>
                                                <Select
                                                    value={item.description}
                                                    label="Descrição / Tipo"
                                                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                                >
                                                    {documentTypes.map((type) => (
                                                        <MenuItem key={type} value={type}>
                                                            {type}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                                            <IconButton size="small" onClick={() => handleRemoveSelected(index)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </ListItem>
                            ))}
                        </List>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                            <Button
                                onClick={() => setSelectedFiles([])}
                                color="inherit"
                                size="small"
                                disabled={isUploading}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirmUpload}
                                disabled={isUploading}
                                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                            >
                                {isUploading ? "Enviando..." : "Enviar Todos"}
                            </Button>
                        </Box>
                    </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, pl: 2 }}>
                    Anexado(s): {documents.length}
                </Typography>

                {/* Documents List */}
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {documents.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                Nenhum anexo encontrado.
                            </Typography>
                        ) : (
                            documents.map((doc) => (
                                <ListItem
                                    key={doc.id}
                                    divider
                                    sx={{
                                        py: 1.5,
                                        px: 1,
                                        '&:hover': { bgcolor: '#f9f9f9' }
                                    }}
                                >
                                    <Box sx={{ mr: 2, color: "#9e9e9e" }}>
                                        <InsertDriveFileIcon fontSize="large" />
                                    </Box>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1" fontWeight="medium">
                                                {doc.description || doc.originalName || doc.filename}
                                            </Typography>
                                        }
                                        secondary={
                                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                                                {doc.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {doc.originalName}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(doc.createdAt).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <Stack direction="row" spacing={1}>
                                        <IconButton size="small" onClick={() => handlePreviewFile(doc)} title="Visualizar">
                                            <VisibilityIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDownload(doc)} title="Baixar">
                                            <FileDownloadIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(doc.id)} title="Excluir">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                </ListItem>
                            ))
                        )}
                    </List>
                )}
            </DialogContent>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmationOpen}
                onClose={() => setDeleteConfirmationOpen(false)}
            >
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza que deseja excluir este anexo? Esta ação não pode ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmationOpen(false)} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={confirmDelete} color="error" autoFocus>
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Modal */}
            {
                previewDoc && (
                    <ModalPreviewDocument
                        open={!!previewDoc}
                        onClose={handleClosePreview}
                        previewDoc={previewDoc}
                        previewUrl={previewUrl}
                        isLoading={isLoadingPreview}
                    />
                )
            }
        </Dialog >
    );
};
